﻿import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import FilterListIcon from "@material-ui/icons/FilterList";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getAdminTexts, getLocale, interpolate } from "../../translate/pageTexts";

const useStyles = makeStyles(theme => ({
  pagePaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "auto",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(11,18,26,0.98) 0%, rgba(8,15,22,0.98) 100%)"
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.12)"
        : "rgba(18, 140, 126, 0.10)",
    ...theme.scrollbarStyles
  },
  introRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  introText: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5)
  },
  pageTitle: {
    fontWeight: 800
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 760
  },
  tabs: {
    marginBottom: theme.spacing(2)
  },
  actionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  historyActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  actionButton: {
    borderRadius: 999,
    minHeight: 42,
    paddingInline: theme.spacing(2),
    fontWeight: 700
  },
  metricsGrid: {
    marginBottom: theme.spacing(2)
  },
  metricCard: {
    padding: theme.spacing(2.25),
    borderRadius: 18,
    minHeight: 112,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19, 31, 43, 0.98) 0%, rgba(13, 24, 34, 0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,249,0.98) 100%)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.16)"
        : "1px solid rgba(18, 140, 126, 0.10)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 14px 32px rgba(0, 0, 0, 0.22)"
        : "none"
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 700
  },
  metricValue: {
    fontSize: "1.9rem",
    fontWeight: 800,
    lineHeight: 1
  },
  iconMuted: {
    color: theme.palette.text.secondary,
    opacity: 0.8
  },
  tablePaper: {
    borderRadius: 20,
    overflow: "hidden",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.16)"
        : "1px solid rgba(18, 140, 126, 0.10)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.98)"
        : theme.palette.background.paper
  },
  tableHeaderCell: {
    fontWeight: 800,
    color: theme.palette.type === "dark" ? "#d7e7f4" : "#334155",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(18, 32, 44, 0.98)"
        : "rgba(248, 250, 252, 0.96)"
  },
  rowName: {
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  rowMeta: {
    marginTop: theme.spacing(0.4),
    color: theme.palette.text.secondary,
    fontSize: "0.86rem"
  },
  protocolButton: {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700
  },
  drawerPaper: {
    width: 360,
    maxWidth: "92vw",
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  drawerFooter: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  filterField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14
    }
  },
  filterSummary: {
    color: theme.palette.text.secondary
  },
  tableCard: {
    padding: theme.spacing(2),
    borderRadius: 18,
    marginBottom: theme.spacing(2),
    overflowX: "auto"
  },
  sectionHeading: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.82)"
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.14)"
        : "rgba(18, 140, 126, 0.10)"
  },
  actionsSection: {
    padding: theme.spacing(2),
    borderRadius: 18,
    marginBottom: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.82)"
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.14)"
        : "rgba(18, 140, 126, 0.10)"
  },
  actionsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5)
  },
  actionsTitle: {
    fontWeight: 700
  },
  actionsDescription: {
    color: theme.palette.text.secondary
  },
  actionsRow: {
    display: "flex",
    gap: theme.spacing(1.25),
    flexWrap: "wrap"
  },
  connectionCard: {
    padding: theme.spacing(2),
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    height: "100%",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.14)"
        : "rgba(18, 140, 126, 0.10)"
  },
  connectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5)
  },
  connectionTitle: {
    fontWeight: 800
  },
  connectionSubtitle: {
    color: theme.palette.text.secondary
  },
  countersRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  counterChip: {
    backgroundColor: "rgba(148, 163, 184, 0.12)",
    color: theme.palette.text.primary
  },
  connectionActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  subtleButton: {
    minWidth: 0
  }
}));

const createDefaultHistoryFilters = () => ({
  ticketId: "",
  searchParam: "",
  contactNumber: "",
  contactEmail: "",
  contactDocument: "",
  customFieldName: "",
  customFieldValue: "",
  queueIds: [],
  userIds: [],
  whatsappIds: [],
  tagIds: [],
  dateFrom: "",
  dateTo: ""
});

const normalizeArrayFilter = value => {
  if (Array.isArray(value)) {
    return value.filter(item => item !== undefined && item !== null && item !== "");
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (value === undefined || value === null || value === "") {
    return [];
  }

  return [value];
};

const normalizeHistoryFilters = value => {
  const source = value && typeof value === "object" ? value : {};

  const asText = key =>
    typeof source[key] === "string" ? source[key] : String(source[key] || "");

  return {
    ticketId: asText("ticketId"),
    searchParam: asText("searchParam"),
    contactNumber: asText("contactNumber"),
    contactEmail: asText("contactEmail"),
    contactDocument: asText("contactDocument"),
    customFieldName: asText("customFieldName"),
    customFieldValue: asText("customFieldValue"),
    queueIds: normalizeArrayFilter(source.queueIds),
    userIds: normalizeArrayFilter(source.userIds),
    whatsappIds: normalizeArrayFilter(source.whatsappIds),
    tagIds: normalizeArrayFilter(source.tagIds),
    dateFrom:
      typeof source.dateFrom === "string" ? source.dateFrom : String(source.dateFrom || ""),
    dateTo: typeof source.dateTo === "string" ? source.dateTo : String(source.dateTo || "")
  };
};

const formatTicketProtocol = ticketId => `#${String(ticketId || "").padStart(6, "0")}`;

const formatDateTime = value => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString(getLocale());
};

const getClosedDisplayDate = ticket => ticket?.closedAt || ticket?.updatedAt;

const getOperationalSummary = ticket => ticket?.operationalSummary || {};

const formatDurationMs = value => {
  const totalSeconds = Math.floor(Math.max(0, Number(value || 0)) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatHandledByMode = (mode, texts = getAdminTexts()) => {
  const normalizedMode = String(mode || "").toLowerCase();
  return texts.attendances.handledModes?.[normalizedMode] || texts.attendances.handledModes?.unknown || "-";
};

const formatRatingLabel = (rating, texts = getAdminTexts()) => {
  if (!rating) return texts.attendances.noRating;
  if (rating.status !== "answered" || !rating.ratingValue) return texts.attendances.noRating;
  return interpolate(texts.attendances.ratingScore, { score: rating.ratingValue });
};

const getAttendantLabel = (ticket, usersById = new Map(), texts = getAdminTexts()) => {
  if (ticket?.user?.name) {
    return ticket.user.name;
  }

  const userId = Number(ticket?.userId || 0);
  if (userId > 0) {
    const fallbackName = usersById.get(userId);
    if (fallbackName) {
      return fallbackName;
    }

    return interpolate(texts.attendances.attendantFallback, { id: userId });
  }

  return texts.common.noAttendant;
};

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const excelTextCell = value =>
  `<td style='mso-number-format:"\\@";'>${escapeHtml(value)}</td>`;

const excelMultilineTextCell = value =>
  `<td style='mso-number-format:"\\@";white-space:pre-wrap;'>${escapeHtml(
    value
  ).replace(/\r?\n/g, "<br/>")}</td>`;

const formatTimelineForExcel = value => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join("\n") || "-";
  }

  return value || "-";
};

// detailed=false gera o sintetico: mesmas colunas, sem a linha do tempo
// operacional. E a coluna mais pesada do arquivo (uma celula multilinha com
// cada interacao do ticket), entao tira-la muda bastante o tamanho e a
// legibilidade da planilha para quem so quer os numeros.
const exportRowsToExcel = (
  rows,
  usersById = new Map(),
  texts = getAdminTexts(),
  { detailed = true } = {}
) => {
  const htmlRows = rows
    .map(
      row => {
        const operationalSummary = getOperationalSummary(row);

        return `
        <tr>
          <td>${escapeHtml(row.contact?.name || texts.common.noContact)}</td>
          <td>${escapeHtml(row.whatsapp?.name || texts.common.noConnection)}</td>
          <td>${escapeHtml(row.queue?.name || texts.common.noQueue)}</td>
          <td>${escapeHtml(getAttendantLabel(row, usersById, texts))}</td>
          <td>${escapeHtml(row.subject || "-")}</td>
          ${excelTextCell(formatDurationMs(operationalSummary.tmeMs))}
          ${excelTextCell(formatDurationMs(operationalSummary.tmaMs))}
          ${excelTextCell(formatDurationMs(operationalSummary.slaMs))}
          <td>${escapeHtml(operationalSummary.transferCount || 0)}</td>
          ${excelTextCell(operationalSummary.queueFlow || "-")}
          ${detailed
            ? excelMultilineTextCell(
                formatTimelineForExcel(operationalSummary.operationalTimeline)
              )
            : ""}
          <td>${escapeHtml(formatRatingLabel(operationalSummary.rating, texts))}</td>
          <td>${escapeHtml(formatDateTime(row.createdAt))}</td>
          <td>${escapeHtml(formatDateTime(getClosedDisplayDate(row)))}</td>
          <td>${escapeHtml(formatTicketProtocol(row.id))}</td>
        </tr>
      `;
      }
    )
    .join("");

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /></head>
      <body>
        <table border="1" cellspacing="0" cellpadding="6">
          <tr>
            <th>${escapeHtml(texts.common.name)}</th>
            <th>${escapeHtml(texts.common.connection)}</th>
            <th>${escapeHtml(texts.attendances.department)}</th>
            <th>${escapeHtml(texts.common.attendant)}</th>
            <th>${escapeHtml(texts.common.subject)}</th>
            <th>${escapeHtml(texts.attendances.tme)}</th>
            <th>${escapeHtml(texts.attendances.tma)}</th>
            <th>${escapeHtml(texts.attendances.slaTime)}</th>
            <th>${escapeHtml(texts.attendances.transfers)}</th>
            <th>${escapeHtml(texts.attendances.queueFlow)}</th>
            ${detailed
              ? `<th>${escapeHtml(texts.attendances.operationalTimeline)}</th>`
              : ""}
            <th>${escapeHtml(texts.attendances.rating)}</th>
            <th>${escapeHtml(texts.attendances.startedAt)}</th>
            <th>${escapeHtml(texts.attendances.closedAt)}</th>
            <th>${escapeHtml(texts.common.protocol)}</th>
          </tr>
          ${htmlRows}
        </table>
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff", html], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `histórico-atendimentos-${
    detailed ? "analitico" : "sintetico"
  }-${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const Attendances = () => {
  const classes = useStyles();
  const texts = getAdminTexts();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("history");
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [loadingManagement, setLoadingManagement] = useState(true);
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    queues: [],
    whatsapps: [],
    tags: [],
    customFieldNames: []
  });
  const [draftHistoryFilters, setDraftHistoryFilters] = useState(() =>
    createDefaultHistoryFilters()
  );
  const [historyFilters, setHistoryFilters] = useState(() =>
    createDefaultHistoryFilters()
  );
  const [historyPage, setHistoryPage] = useState(0);
  const [historyData, setHistoryData] = useState({
    tickets: [],
    count: 0
  });
  const [summary, setSummary] = useState({
    totals: { connections: 0, open: 0, pending: 0, closed: 0 },
    items: []
  });
  const [workload, setWorkload] = useState(null);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState({
    operation: "close",
    title: "",
    message: "",
    payload: null
  });

  const fetchWorkload = useCallback(async () => {
    setLoadingWorkload(true);
    try {
      const { data } = await api.get("/tickets/attendance-summary");
      setWorkload(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingWorkload(false);
    }
  }, []);

  // Carrega so quando a aba e aberta, e nao junto com a pagina: quem entra em
  // Atendimentos para consultar o historico nao precisa pagar por uma
  // agregacao sobre todos os tickets abertos.
  useEffect(() => {
    if (activeTab === "workload" && !workload) {
      fetchWorkload();
    }
  }, [activeTab, workload, fetchWorkload]);

  // Os mesmos filtros alimentam a listagem e a exportacao. Centralizado para as
  // duas nao divergirem: um filtro novo que so entrasse na listagem produziria
  // um arquivo exportado com escopo diferente do que esta na tela.
  const buildHistoryParams = useCallback(filters => ({
    status: "closed",
    showAll: "true",
    includeOperationalSummary: "true",
    ...(filters.ticketId ? { ticketId: filters.ticketId } : {}),
    ...(filters.searchParam ? { searchParam: filters.searchParam } : {}),
    ...(filters.contactNumber ? { contactNumber: filters.contactNumber } : {}),
    ...(filters.contactEmail ? { contactEmail: filters.contactEmail } : {}),
    ...(filters.contactDocument ? { contactDocument: filters.contactDocument } : {}),
    ...(filters.customFieldName ? { customFieldName: filters.customFieldName } : {}),
    ...(filters.customFieldValue ? { customFieldValue: filters.customFieldValue } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(filters.queueIds.length ? { queueIds: JSON.stringify(filters.queueIds) } : {}),
    ...(filters.userIds.length ? { userIds: JSON.stringify(filters.userIds) } : {}),
    ...(filters.whatsappIds.length
      ? { whatsappIds: JSON.stringify(filters.whatsappIds) }
      : {}),
    ...(filters.tagIds.length ? { tagIds: JSON.stringify(filters.tagIds) } : {})
  }), []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const safeHistoryFilters = normalizeHistoryFilters(historyFilters);
      const { data } = await api.get("/tickets", {
        params: {
          ...buildHistoryParams(safeHistoryFilters),
          pageNumber: String(historyPage + 1)
        }
      });

      setHistoryData({
        tickets: Array.isArray(data?.tickets) ? data.tickets : [],
        count: Number(data?.count || 0)
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyFilters, historyPage, buildHistoryParams]);


  const fetchFilterOptions = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets/filter-options");
      setFilterOptions(current => ({
        ...current,
        users: Array.isArray(data?.users) ? data.users : [],
        queues: Array.isArray(data?.queues) ? data.queues : [],
        whatsapps: Array.isArray(data?.whatsapps) ? data.whatsapps : [],
        tags: Array.isArray(data?.tags) ? data.tags : []
      }));

      // Nomes de campos adicionais vem do endpoint de contatos, o mesmo que a
      // tela de Contatos usa - sem isto o usuario teria de saber de cor o nome
      // exato cadastrado. Falha aqui nao derruba os demais filtros.
      try {
        const { data: names } = await api.get("/contacts/custom-field-names");
        setFilterOptions(current => ({
          ...current,
          customFieldNames: Array.isArray(names) ? names.filter(Boolean) : []
        }));
      } catch (customFieldErr) {
        setFilterOptions(current => ({ ...current, customFieldNames: [] }));
      }
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchManagementSummary = useCallback(async () => {
    setLoadingManagement(true);
    try {
      const { data } = await api.get("/whatsapp/attendance/summary");
      setSummary(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingManagement(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchManagementSummary();
  }, [fetchManagementSummary]);

  const totals = useMemo(() => summary.totals || {}, [summary]);
  const usersById = useMemo(() => {
    const entries = (filterOptions.users || [])
      .map(user => [Number(user.id), String(user.name || "").trim()])
      .filter(([id, name]) => id > 0 && Boolean(name));
    return new Map(entries);
  }, [filterOptions.users]);

  // Exportacao varre TODAS as paginas do filtro atual, nao so a que esta na tela.
  // Blocos grandes de proposito: o enriquecimento operacional custa 5 consultas
  // por pagina independentemente do tamanho dela, entao pagina maior significa
  // menos consultas no total, e nao mais.
  const handleExport = useCallback(async (detailed = true) => {
    const EXPORT_PAGE_SIZE = 500;
    const EXPORT_MAX_ROWS = 5000;

    setExporting(true);
    setExportProgress(0);

    try {
      const safeHistoryFilters = normalizeHistoryFilters(historyFilters);
      const baseParams = buildHistoryParams(safeHistoryFilters);
      const collected = [];
      let page = 1;
      let total = 0;

      for (;;) {
        const { data } = await api.get("/tickets", {
          params: {
            ...baseParams,
            pageNumber: String(page),
            pageSize: String(EXPORT_PAGE_SIZE),
            // Sem isto a ordem e por updatedAt, que muda enquanto varremos as
            // paginas: linhas se deslocam, umas repetem e outras somem do arquivo.
            stableOrder: "true"
          }
        });

        const rows = Array.isArray(data?.tickets) ? data.tickets : [];
        total = Number(data?.count || 0);
        collected.push(...rows);

        setExportProgress(
          total > 0 ? Math.min(100, Math.round((collected.length / Math.min(total, EXPORT_MAX_ROWS)) * 100)) : 100
        );

        if (!rows.length || collected.length >= total || collected.length >= EXPORT_MAX_ROWS) {
          break;
        }

        page += 1;
      }

      if (!collected.length) {
        toast.info(texts.attendances.exportEmpty);
        return;
      }

      exportRowsToExcel(collected, usersById, texts, { detailed });

      // Truncar em silencio seria pior que falhar: o arquivo pareceria completo.
      if (total > collected.length) {
        toast.warn(
          interpolate(texts.attendances.exportTruncated, {
            exported: collected.length,
            total
          })
        );
      } else {
        toast.success(
          interpolate(texts.attendances.exportDone, { count: collected.length })
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  }, [historyFilters, buildHistoryParams, usersById, texts]);
  const historyOperationalStats = useMemo(() => {
    const summaries = historyData.tickets.map(getOperationalSummary);
    const totalTransfers = summaries.reduce(
      (sum, item) => sum + Number(item.transferCount || 0),
      0
    );
    const totalSlaMs = summaries.reduce(
      (sum, item) => sum + Number(item.slaMs || 0),
      0
    );
    const summariesWithTme = summaries.filter(item => Number(item.tmeMs || 0) > 0);
    const summariesWithTma = summaries.filter(item => Number(item.tmaMs || 0) > 0);
    const totalTmeMs = summariesWithTme.reduce(
      (sum, item) => sum + Number(item.tmeMs || 0),
      0
    );
    const totalTmaMs = summariesWithTma.reduce(
      (sum, item) => sum + Number(item.tmaMs || 0),
      0
    );
    const answeredRatings = summaries
      .map(item => item.rating)
      .filter(rating => rating?.status === "answered" && Number(rating.ratingValue || 0) > 0);
    const averageRating =
      answeredRatings.length > 0
        ? (
            answeredRatings.reduce(
              (sum, rating) => sum + Number(rating.ratingValue || 0),
              0
            ) / answeredRatings.length
          ).toFixed(1)
        : "-";

    return {
      totalTransfers,
      averageSla:
        summaries.length > 0
          ? formatDurationMs(totalSlaMs / summaries.length)
          : "-",
      averageTme:
        summariesWithTme.length > 0
          ? formatDurationMs(totalTmeMs / summariesWithTme.length)
          : "-",
      averageTma:
        summariesWithTma.length > 0
          ? formatDurationMs(totalTmaMs / summariesWithTma.length)
          : "-",
      averageRating
    };
  }, [historyData.tickets]);

  const safeHistoryFilters = useMemo(
    () => normalizeHistoryFilters(historyFilters),
    [historyFilters]
  );

  const activeFiltersCount = useMemo(
    () =>
      [
        safeHistoryFilters.ticketId,
        safeHistoryFilters.searchParam,
        safeHistoryFilters.contactNumber,
        safeHistoryFilters.contactEmail,
        safeHistoryFilters.contactDocument,
        // Nome sozinho nao filtra nada sem valor, entao contar os dois separado
        // inflaria o badge: o par conta como um filtro so.
        safeHistoryFilters.customFieldName || safeHistoryFilters.customFieldValue,
        safeHistoryFilters.dateFrom,
        safeHistoryFilters.dateTo,
        safeHistoryFilters.queueIds.length,
        safeHistoryFilters.userIds.length,
        safeHistoryFilters.whatsappIds.length,
        safeHistoryFilters.tagIds.length
      ].filter(Boolean).length,
    [safeHistoryFilters]
  );

  const buildScopeAndAffected = payload => {
    const scopeLabel = payload.whatsappId
      ? interpolate(texts.attendances.connectionScope, { name: payload.name })
      : texts.attendances.allConnections;

    const affected = [
      payload.open ? `"${texts.attendances.open}"` : null,
      payload.pending ? `"${texts.attendances.pending}"` : null
    ]
      .filter(Boolean)
      .join(" e ");

    return { scopeLabel, affected };
  };

  const handleRequestClose = payload => {
    const { scopeLabel, affected } = buildScopeAndAffected(payload);

    setConfirmInfo({
      operation: "close",
      title: texts.attendances.confirmCloseTitle,
      message: interpolate(texts.attendances.confirmCloseMessage, {
        affected,
        scope: scopeLabel
      }),
      payload
    });
    setConfirmOpen(true);
  };

  const handleRequestDelete = payload => {
    const { scopeLabel, affected } = buildScopeAndAffected(payload);

    setConfirmInfo({
      operation: "delete",
      title: texts.attendances.confirmDeleteTitle,
      message: interpolate(texts.attendances.confirmDeleteMessage, {
        affected,
        scope: scopeLabel
      }),
      payload
    });
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmInfo.payload) return;

    try {
      if (confirmInfo.operation === "delete") {
        await api.post("/whatsapp/attendance/delete", {
          whatsappId: confirmInfo.payload.whatsappId,
          deleteOpen: confirmInfo.payload.open,
          deletePending: confirmInfo.payload.pending
        });
        toast.success(texts.attendances.deletedSuccess);
      } else {
        await api.post("/whatsapp/attendance/close", {
          whatsappId: confirmInfo.payload.whatsappId,
          closeOpen: confirmInfo.payload.open,
          closePending: confirmInfo.payload.pending
        });
        toast.success(texts.attendances.updatedSuccess);
      }
      fetchManagementSummary();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDraftFilterChange = field => event => {
    const rawValue = event?.target?.value;
    const isArrayField = ["queueIds", "userIds", "whatsappIds", "tagIds"].includes(field);
    const nextValue = isArrayField ? normalizeArrayFilter(rawValue) : String(rawValue || "");

    setDraftHistoryFilters(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const handleApplyFilters = () => {
    const normalized = normalizeHistoryFilters(draftHistoryFilters);
    setHistoryPage(0);
    setDraftHistoryFilters(normalized);
    setHistoryFilters(normalized);
    setHistoryFiltersOpen(false);
  };

  const handleClearFilters = () => {
    const cleared = createDefaultHistoryFilters();
    setHistoryPage(0);
    setDraftHistoryFilters(cleared);
    setHistoryFilters(cleared);
  };

  const renderMetricCard = (label, value, icon) => (
    <Paper variant="outlined" className={classes.metricCard}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className={classes.metricLabel}>{label}</Typography>
        <span className={classes.iconMuted}>{icon}</span>
      </Box>
      <Typography className={classes.metricValue}>{value}</Typography>
    </Paper>
  );

  const openTicket = ticketId => {
    history.push(`/tickets/${ticketId}`);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmInfo.title}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleConfirmAction}
      >
        {confirmInfo.message}
      </ConfirmationModal>

      <Drawer
        anchor="right"
        open={historyFiltersOpen}
        onClose={() => setHistoryFiltersOpen(false)}
        classes={{ paper: classes.drawerPaper }}
      >
        <div>
          <Typography variant="h6">{texts.attendances.filtersTitle}</Typography>
          <Typography variant="body2" className={classes.filterSummary}>
            {texts.attendances.filtersSubtitle}
          </Typography>
        </div>

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.ticketId}
          placeholder={texts.attendances.ticketIdPlaceholder}
          value={draftHistoryFilters.ticketId}
          onChange={handleDraftFilterChange("ticketId")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.search}
          placeholder={texts.attendances.searchPlaceholder}
          value={draftHistoryFilters.searchParam}
          onChange={handleDraftFilterChange("searchParam")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.contactDocument}
          placeholder={texts.attendances.contactDocumentPlaceholder}
          value={draftHistoryFilters.contactDocument}
          onChange={handleDraftFilterChange("contactDocument")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.contactNumber}
          placeholder={texts.attendances.contactNumberPlaceholder}
          value={draftHistoryFilters.contactNumber}
          onChange={handleDraftFilterChange("contactNumber")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.contactEmail}
          placeholder={texts.attendances.contactEmailPlaceholder}
          value={draftHistoryFilters.contactEmail}
          onChange={handleDraftFilterChange("contactEmail")}
        />

        <TextField
          select
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.customField}
          value={draftHistoryFilters.customFieldName}
          onChange={handleDraftFilterChange("customFieldName")}
          helperText={
            filterOptions.customFieldNames.length
              ? texts.attendances.customFieldHelper
              : texts.attendances.customFieldEmpty
          }
        >
          <MenuItem value="">{texts.attendances.customFieldAny}</MenuItem>
          {filterOptions.customFieldNames.map(name => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.customFieldValue}
          placeholder={texts.attendances.customFieldValuePlaceholder}
          value={draftHistoryFilters.customFieldValue}
          onChange={handleDraftFilterChange("customFieldValue")}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label={texts.attendances.from}
          type="date"
          value={draftHistoryFilters.dateFrom}
          onChange={handleDraftFilterChange("dateFrom")}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          className={classes.filterField}
          variant="outlined"
          label="Até"
          type="date"
          value={draftHistoryFilters.dateTo}
          onChange={handleDraftFilterChange("dateTo")}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label={texts.common.queue}
          value={draftHistoryFilters.queueIds}
          onChange={handleDraftFilterChange("queueIds")}
        >
          {filterOptions.queues.map(queue => (
            <MenuItem key={queue.id} value={queue.id}>
              {queue.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label={texts.common.attendant}
          value={draftHistoryFilters.userIds}
          onChange={handleDraftFilterChange("userIds")}
        >
          {filterOptions.users.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label={texts.common.connection}
          value={draftHistoryFilters.whatsappIds}
          onChange={handleDraftFilterChange("whatsappIds")}
        >
          {filterOptions.whatsapps.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          SelectProps={{ multiple: true }}
          className={classes.filterField}
          variant="outlined"
          label="Tag"
          value={draftHistoryFilters.tagIds}
          onChange={handleDraftFilterChange("tagIds")}
        >
          {filterOptions.tags.map(tag => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </TextField>

        <div className={classes.drawerFooter}>
          <Button onClick={handleClearFilters}>{texts.common.clear}</Button>
          <Box display="flex" gridGap={8}>
            <Button onClick={() => setHistoryFiltersOpen(false)}>{texts.common.cancel}</Button>
            <Button color="primary" variant="contained" onClick={handleApplyFilters}>
              {texts.common.apply}
            </Button>
          </Box>
        </div>
      </Drawer>

      <MainHeader>
        <Title>{texts.attendances.title}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchHistory();
              fetchManagementSummary();
            }}
          >
            {texts.common.refresh}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pagePaper} variant="outlined">
        <div className={classes.introRow}>
          <div className={classes.introText}>
            <Typography variant="h5" className={classes.pageTitle}>
              {texts.attendances.historyTitle}
            </Typography>
            <Typography variant="body2" className={classes.pageSubtitle}>
              {texts.attendances.historySubtitle}
            </Typography>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab value="history" icon={<HistoryOutlinedIcon />} label={texts.attendances.historyTab} />
          <Tab
            value="management"
            icon={<AssignmentTurnedInOutlinedIcon />}
            label={texts.attendances.managementTab}
          />
          <Tab
            value="workload"
            icon={<ForumOutlinedIcon />}
            label="Resumo por fila e atendente"
          />
        </Tabs>

        {activeTab === "history" ? (
          <>
            <div className={classes.actionRow}>
              <Typography variant="body2" className={classes.filterSummary}>
                {activeFiltersCount
                  ? interpolate(texts.attendances.activeFilters, { count: activeFiltersCount })
                  : texts.attendances.noActiveFilters}
              </Typography>
              <div className={classes.historyActions}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  className={classes.actionButton}
                  onClick={() => setHistoryFiltersOpen(true)}
                >
                  {texts.attendances.showFilters}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  className={classes.actionButton}
                  onClick={event => setExportMenuAnchor(event.currentTarget)}
                  disabled={!historyData.tickets.length || exporting}
                >
                  {exporting
                    ? interpolate(texts.attendances.exporting, {
                        progress: exportProgress
                      })
                    : texts.attendances.export}
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={() => setExportMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setExportMenuAnchor(null);
                      handleExport(true);
                    }}
                  >
                    {texts.attendances.exportDetailed}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setExportMenuAnchor(null);
                      handleExport(false);
                    }}
                  >
                    {texts.attendances.exportSummary}
                  </MenuItem>
                </Menu>
              </div>
            </div>

            <Grid container spacing={2} className={classes.metricsGrid}>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.loadedClosed,
                  historyData.tickets.length,
                  <AssignmentTurnedInOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.totalFound,
                  historyData.count,
                  <HistoryOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.averageTme,
                  historyOperationalStats.averageTme,
                  <ScheduleOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.averageTma,
                  historyOperationalStats.averageTma,
                  <ScheduleOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.averageSla,
                  historyOperationalStats.averageSla,
                  <ScheduleOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.transfers,
                  historyOperationalStats.totalTransfers,
                  <ForumOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.averageRating,
                  historyOperationalStats.averageRating,
                  <AssignmentTurnedInOutlinedIcon />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderMetricCard(
                  texts.attendances.withAttendant,
                  historyData.tickets.filter(
                    ticket => getAttendantLabel(ticket, usersById, texts) !== texts.common.noAttendant
                  ).length,
                  <PersonOutlineOutlinedIcon />
                )}
              </Grid>
            </Grid>

            <Paper className={classes.tablePaper}>
              {loadingHistory ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={260}
                >
                  <CircularProgress />
                </Box>
              ) : historyData.tickets.length ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.common.name}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.common.connection}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.attendances.department}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.common.attendant}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.common.subject}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.attendances.operation}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.attendances.rating}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.attendances.closedAt}
                          </TableCell>
                          <TableCell className={classes.tableHeaderCell}>
                            {texts.attendances.protocolNumber}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyData.tickets.map(ticket => (
                          <TableRow hover key={ticket.id}>
                            {(() => {
                              const operationalSummary = getOperationalSummary(ticket);
                              const rating = operationalSummary.rating;

                              return (
                                <>
                            <TableCell>
                              <Typography className={classes.rowName}>
                                {ticket.contact?.name || texts.common.noContact}
                              </Typography>
                              <Typography className={classes.rowMeta}>
                                {ticket.contact?.number || texts.common.unavailableNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ticket.whatsapp?.name || texts.common.noConnection}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ticket.queue?.name || texts.common.noQueue}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getAttendantLabel(ticket, usersById, texts)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {ticket.subject || "-"}
                              </Typography>
                              {ticket.resolutionSummary ? (
                                <Typography className={classes.rowMeta}>
                                  {ticket.resolutionSummary}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" flexDirection="column" gridGap={6}>
                                <Typography className={classes.rowMeta}>
                                  {texts.attendances.tme}: {formatDurationMs(operationalSummary.tmeMs)}
                                </Typography>
                                <Typography className={classes.rowMeta}>
                                  {texts.attendances.tma}: {formatDurationMs(operationalSummary.tmaMs)}
                                </Typography>
                                <Typography className={classes.rowMeta}>
                                  {texts.attendances.slaTime}: {formatDurationMs(operationalSummary.slaMs)}
                                </Typography>
                                <Typography className={classes.rowMeta}>
                                  {texts.attendances.totalTime}: {formatDurationMs(operationalSummary.totalMs)}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                  <Chip
                                    size="small"
                                    label={interpolate(texts.attendances.transferCount, {
                                      count: operationalSummary.transferCount || 0
                                    })}
                                  />
                                  <Chip
                                    size="small"
                                    label={formatHandledByMode(
                                      operationalSummary.handledByMode,
                                      texts
                                    )}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatRatingLabel(rating, texts)}
                                color={
                                  rating?.status === "answered" && rating?.ratingValue
                                    ? "primary"
                                    : "default"
                                }
                              />
                              {rating?.comment ? (
                                <Typography className={classes.rowMeta}>
                                  {rating.comment}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Typography>
                                {formatDateTime(getClosedDisplayDate(ticket))}
                              </Typography>
                              <Typography className={classes.rowMeta}>
                                {texts.attendances.startedAt}: {formatDateTime(ticket.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                color="primary"
                                endIcon={<OpenInNewIcon />}
                                className={classes.protocolButton}
                                onClick={() => openTicket(ticket.id)}
                              >
                                {formatTicketProtocol(ticket.id)}
                              </Button>
                            </TableCell>
                                </>
                              );
                            })()}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={historyData.count}
                    rowsPerPage={40}
                    rowsPerPageOptions={[40]}
                    page={historyPage}
                    onPageChange={(_, page) => setHistoryPage(page)}
                  />
                </>
              ) : (
                <Paper variant="outlined" className={classes.emptyState}>
                  {texts.attendances.emptyHistory}
                </Paper>
              )}
            </Paper>
          </>
        ) : activeTab === "management" ? (
          <>
            {loadingManagement ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={240}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} className={classes.metricsGrid}>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      texts.attendances.connections,
                      totals.connections || 0,
                      <ForumOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      texts.attendances.open,
                      totals.open || 0,
                      <AssignmentTurnedInOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      texts.attendances.pending,
                      totals.pending || 0,
                      <ScheduleOutlinedIcon />
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {renderMetricCard(
                      texts.attendances.resolved,
                      totals.closed || 0,
                      <AssignmentTurnedInOutlinedIcon />
                    )}
                  </Grid>
                </Grid>

                <Paper variant="outlined" className={classes.actionsSection}>
                  <div className={classes.actionsHeader}>
                    <div>
                      <Typography className={classes.actionsTitle}>
                        {texts.attendances.globalActions}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.actionsDescription}
                      >
                        {texts.attendances.globalDescription}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.actionsRow}>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        handleRequestClose({
                          open: true,
                          pending: false
                        })
                      }
                    >
                      {texts.attendances.closeOpen}
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        handleRequestClose({
                          open: false,
                          pending: true
                        })
                      }
                    >
                      {texts.attendances.closePending}
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        handleRequestClose({
                          open: true,
                          pending: true
                        })
                      }
                    >
                      {texts.attendances.closeAll}
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        handleRequestDelete({
                          open: true,
                          pending: false
                        })
                      }
                    >
                      {texts.attendances.deleteOpen}
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        handleRequestDelete({
                          open: false,
                          pending: true
                        })
                      }
                    >
                      {texts.attendances.deletePending}
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.subtleButton}`}
                      variant="contained"
                      color="secondary"
                      onClick={() =>
                        handleRequestDelete({
                          open: true,
                          pending: true
                        })
                      }
                    >
                      {texts.attendances.deleteAll}
                    </Button>
                  </div>
                </Paper>

                {summary.items?.length ? (
                  <Grid container spacing={2}>
                    {summary.items.map(item => (
                      <Grid item xs={12} md={6} lg={4} key={item.id}>
                        <Paper variant="outlined" className={classes.connectionCard}>
                          <div className={classes.connectionHeader}>
                            <div>
                              <Typography className={classes.connectionTitle}>
                                {item.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                className={classes.connectionSubtitle}
                              >
                                {interpolate(texts.attendances.connectionStatus, { status: item.status })}
                              </Typography>
                            </div>
                            <Chip
                              size="small"
                              variant={
                                item.allowGroupMessages ? "default" : "outlined"
                              }
                              className={
                                item.allowGroupMessages
                                  ? classes.counterChip
                                  : undefined
                              }
                              label={
                                item.allowGroupMessages
                                  ? texts.attendances.groupsActive
                                  : texts.attendances.groupsDisabled
                              }
                            />
                          </div>

                          <Divider />

                          <div className={classes.countersRow}>
                            <Chip
                              className={classes.counterChip}
                              label={interpolate(texts.attendances.openCount, { count: item.openCount })}
                            />
                            <Chip
                              className={classes.counterChip}
                              label={interpolate(texts.attendances.pendingCount, { count: item.pendingCount })}
                            />
                            <Chip
                              className={classes.counterChip}
                              label={interpolate(texts.attendances.resolvedCount, { count: item.closedCount })}
                            />
                          </div>

                          <div className={classes.connectionActions}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: true,
                                  pending: false
                                })
                              }
                            >
                              {texts.attendances.closeOpen}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: false,
                                  pending: true
                                })
                              }
                            >
                              {texts.attendances.closePending}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestClose({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: true,
                                  pending: true
                                })
                              }
                            >
                              {texts.attendances.closeAllPlural}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestDelete({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: true,
                                  pending: false
                                })
                              }
                            >
                              {texts.attendances.deleteOpen}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestDelete({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: false,
                                  pending: true
                                })
                              }
                            >
                              {texts.attendances.deletePending}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              className={classes.subtleButton}
                              onClick={() =>
                                handleRequestDelete({
                                  whatsappId: item.id,
                                  name: item.name,
                                  open: true,
                                  pending: true
                                })
                              }
                            >
                              {texts.attendances.deleteAllPlural}
                            </Button>
                          </div>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper variant="outlined" className={classes.emptyState}>
                    {texts.attendances.emptyManagement}
                  </Paper>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div className={classes.actionRow}>
              <Typography variant="body2" className={classes.filterSummary}>
                Carga aberta no momento, por fila e por atendente.
                {workload?.generatedAt
                  ? ` Atualizado às ${formatDateTime(workload.generatedAt)}.`
                  : ""}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchWorkload}
                disabled={loadingWorkload}
              >
                {texts.common.refresh}
              </Button>
            </div>

            {loadingWorkload && !workload ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={240}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} className={classes.metricsGrid}>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" className={classes.metricCard}>
                      <Typography variant="body2" className={classes.metricLabel}>
                        Em atendimento
                      </Typography>
                      <Typography className={classes.metricValue}>
                        {workload?.totals?.openCount ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" className={classes.metricCard}>
                      <Typography variant="body2" className={classes.metricLabel}>
                        Aguardando na fila
                      </Typography>
                      <Typography className={classes.metricValue}>
                        {workload?.totals?.pendingCount ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" className={classes.metricCard}>
                      <Typography variant="body2" className={classes.metricLabel}>
                        Com mensagens não lidas
                      </Typography>
                      <Typography className={classes.metricValue}>
                        {workload?.totals?.unreadCount ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper variant="outlined" className={classes.tableCard}>
                  <Typography variant="h6" className={classes.sectionHeading}>
                    Por fila de atendimento
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fila</TableCell>
                        <TableCell align="center">Em atendimento</TableCell>
                        <TableCell align="center">Aguardando</TableCell>
                        <TableCell align="center">Não lidos</TableCell>
                        <TableCell align="center">Mais antigo</TableCell>
                        <TableCell align="center">Tempo médio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(workload?.byQueue || []).map(row => (
                        <TableRow key={`queue-${row.id ?? "none"}`}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="center">{row.openCount}</TableCell>
                          <TableCell align="center">{row.pendingCount}</TableCell>
                          <TableCell align="center">{row.unreadCount}</TableCell>
                          <TableCell align="center">
                            {row.openCount + row.pendingCount > 0
                              ? formatDurationMs(row.oldestAgeMs)
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row.openCount + row.pendingCount > 0
                              ? formatDurationMs(row.averageAgeMs)
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!(workload?.byQueue || []).length && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            Nenhuma fila cadastrada.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Paper variant="outlined" className={classes.tableCard}>
                  <Typography variant="h6" className={classes.sectionHeading}>
                    Por atendente
                  </Typography>
                  <Typography variant="body2" className={classes.filterSummary}>
                    Considera apenas chamados em atendimento, já atribuídos. O que está
                    aguardando na fila ainda não tem responsável e aparece só no bloco acima.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Atendente</TableCell>
                        <TableCell align="center">Situação</TableCell>
                        <TableCell align="center">Em atendimento</TableCell>
                        <TableCell align="center">Não lidos</TableCell>
                        <TableCell align="center">Mais antigo</TableCell>
                        <TableCell align="center">Tempo médio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(workload?.byUser || []).map(row => (
                        <TableRow key={`user-${row.id ?? "none"}`}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="center">
                            {row.presence === "online"
                              ? "Online"
                              : row.presence === "away"
                              ? "Ausente"
                              : row.presence === "offline"
                              ? "Offline"
                              : "-"}
                          </TableCell>
                          <TableCell align="center">{row.openCount}</TableCell>
                          <TableCell align="center">{row.unreadCount}</TableCell>
                          <TableCell align="center">
                            {row.openCount > 0 ? formatDurationMs(row.oldestAgeMs) : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row.openCount > 0 ? formatDurationMs(row.averageAgeMs) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!(workload?.byUser || []).length && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            Nenhum atendente cadastrado.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            )}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Attendances;
